import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { switchMap, withLatestFrom, catchError } from 'rxjs/operators';
import { LoggerService } from 'src/app/modules/core/services/logger/logger.service';
import { NotificationPrefsApiService } from './notification-prefs-api.service';
import { LanguageApiService } from 'src/app/modules/core/languages/language-api.service';
import * as notificationPrefsActions from './notification-prefs.actions';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import * as fromRouting from '../../core/store/routing';
import * as fromPrefs from './';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';
import { BroadcastService } from '@app/core/services/broadcast.service';

@Injectable()
export class NotificationPrefsEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<any>,
    private languageApiService: LanguageApiService,
    private loggerService: LoggerService,
    private notificationPrefsApiService: NotificationPrefsApiService,
    private translateService: TranslateService,
    private notify: SystemNotificationsService,
    private cookieService: CookieService,
    private broadcast: BroadcastService
  ) {}

  loadAvailableLanguagesForLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationPrefsActions.loadAvailableLanguagesForLogin),
      switchMap(action => {
        return this.languageApiService.getLoginLanguages().pipe(
          map(codes => notificationPrefsActions.loadAvailableLanguagesForLoginComplete({ languages: codes })),
          catchError(err => of(notificationPrefsActions.setError({ err })))
        );
      })
    )
  );

  loadAvailableLanguages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationPrefsActions.loadAvailableLanguages),
      switchMap(action => {
        return this.languageApiService.getOrganizationLanguages(action.organizationUId).pipe(
          map(codes => notificationPrefsActions.loadAvailableLanguagesComplete({ languages: codes })),
          catchError(err => of(notificationPrefsActions.setError({ err })))
        );
      })
    )
  );

  sendOneTimeAuthEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationPrefsActions.sendOneTimeAuthEmail),
      switchMap(action => {
        return this.notificationPrefsApiService
          .sendOneTimeAuthEmail(action.orgCode, action.email, action.cultureCode, action.recaptchaToken)
          .pipe(
            switchMap(() => [
              notificationPrefsActions.sendOnTimeAuthEmailComplete({ email: action.email }),
              notificationPrefsActions.showVerifyCodeEmail()
            ]),
            catchError(err => of(notificationPrefsActions.setError({ err })))
          );
      })
    )
  );

  sendOneTimeAuthPhone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationPrefsActions.sendOneTimeAuthPhone),
      switchMap(action => {
        return this.notificationPrefsApiService
          .sendOneTimeAuthPhone(action.orgCode, action.phone, action.cultureCode, action.recaptchaToken)
          .pipe(
            switchMap(() => [
              notificationPrefsActions.sendOnTimeAuthPhoneComplete({ phone: action.phone }),
              notificationPrefsActions.showVerifyCodePhone()
            ]),
            catchError(err => of(notificationPrefsActions.setError({ err })))
          );
      })
    )
  );

  // sendOneTimeAuthPhone$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(notificationPrefsActions.sendOneTimeAuthPhone),
  //     switchMap(action => {
  //       return this.notificationPrefsApiService.checkSmsOptIn(action.orgCode, action.phone, action.recaptchaToken).pipe(
  //         switchMap(optInData => {
  //           return [{ action, optInData }];
  //         })
  //       );
  //     }),
  //     switchMap(({ action, optInData }) => {
  //       if (!!optInData.hasOptedIn) {
  //         return this.notificationPrefsApiService
  //           .sendOneTimeAuthPhone(action.orgCode, action.phone, action.cultureCode, action.recaptchaToken)
  //           .pipe(
  //             switchMap(() => [
  //               notificationPrefsActions.sendOnTimeAuthPhoneComplete({ phone: action.phone }),
  //               notificationPrefsActions.showVerifyCodePhone()
  //             ]),
  //             catchError(err => of(notificationPrefsActions.setError({ err })))
  //           );
  //       } else {
  //         return [fromPrefs.showOptInSmsPhone({ phone: action.phone })];
  //       }
  //     }),
  //     catchError(err => of(notificationPrefsActions.setError({ err })))
  //   )
  // );

  optInSmsAuthentication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationPrefsActions.optInSmsAuthentication),
      switchMap(action => {
        return this.notificationPrefsApiService.storeSmsOptIn(action.orgCode, action.phone, action.recaptchaToken).pipe(
          switchMap(() => [notificationPrefsActions.sendOneTimeAuthPhone(action)]),
          catchError(err => of(notificationPrefsActions.setError({ err })))
        );
      })
    )
  );

  sendOneTimeAuthWhatsApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationPrefsActions.sendOneTimeAuthWhatsApp),
      switchMap(action => {
        return this.notificationPrefsApiService
          .sendOneTimeAuthWhatsApp(action.orgCode, action.phone, action.cultureCode, action.recaptchaToken)
          .pipe(
            map(() => notificationPrefsActions.sendOneTimeAuthWhatsAppComplete()),
            catchError(err => of(notificationPrefsActions.setError({ err })))
          );
      })
    )
  );

  processLoginAuthCodeForEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationPrefsActions.processLoginAuthCodeForEmail),
      withLatestFrom(this.store$.select(fromPrefs.selectEmail)),
      switchMap(([action, email]) => {
        return this.notificationPrefsApiService.processAuthCode(action.orgCode, action.code, email, action.cultureCode).pipe(
          map(authToken => {
            return notificationPrefsActions.processLoginAuthCodeForEmailComplete({ authToken });
          }),
          catchError(err => of(notificationPrefsActions.setError({ err })))
        );
      })
    )
  );

  processLoginAuthCodeForPhone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationPrefsActions.processLoginAuthCodeForPhone),
      withLatestFrom(this.store$.select(fromPrefs.selectPhone)),
      switchMap(([action, phone]) => {
        return this.notificationPrefsApiService.processAuthCode(action.orgCode, action.code, phone, action.cultureCode).pipe(
          map(authToken => {
            return notificationPrefsActions.processLoginAuthCodeForEmailComplete({ authToken });
          }),
          catchError(err => of(notificationPrefsActions.setError({ err })))
        );
      })
    )
  );

  getNotificationPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationPrefsActions.getNotificationPreferences),
      switchMap(action => {
        return this.notificationPrefsApiService.getNotificationPreferences().pipe(
          map(preferences => {
            return notificationPrefsActions.getNotificationPreferencesComplete({ preferences });
          }),
          catchError(err => of(notificationPrefsActions.setError({ err })))
        );
      })
    )
  );

  updateNotificationPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationPrefsActions.updateNotificationPreferences),
      switchMap(action => {
        return this.notificationPrefsApiService.updateNotificationPreferences(action.preferences).pipe(
          map(preferences => {
            const message = this.translateService.instant('NOTIFICATION-PREFERENCES.PREFERENCES-SAVED');
            this.notify.success(message);

            if (action.close === true) {
              this.broadcast.sendMessage({ type: 'USER_PROFILE_REFRESH' });
              window.top.close();
            }

            return notificationPrefsActions.updateNotificationPreferencesComplete({ preferences });
          }),
          catchError(err => {
            const message = this.translateService.instant('NOTIFICATION-PREFERENCES.ERROR-SAVE-FAILED');
            this.notify.error(message);
            return of(notificationPrefsActions.setError({ err }));
          })
        );
      })
    )
  );

  processAuthCodeForEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationPrefsActions.processAuthCodeForEmail),
      switchMap(action => {
        return this.notificationPrefsApiService.processAuthCodeForField(action.orgCode, action.code, action.userKey, action.fieldName).pipe(
          map(preferences => {
            const message = this.translateService.instant('NOTIFICATION-PREFERENCES.PREFERENCES-SAVED');
            this.notify.success(message);

            return notificationPrefsActions.processAuthCodeForEmailComplete({ preferences });
          }),
          catchError(err => of(notificationPrefsActions.setError({ err })))
        );
      })
    )
  );

  processAuthCodeForPhone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationPrefsActions.processAuthCodeForPhone),
      switchMap(action => {
        return this.notificationPrefsApiService.processAuthCodeForField(action.orgCode, action.code, action.userKey, action.fieldName).pipe(
          map(preferences => {
            const message = this.translateService.instant('NOTIFICATION-PREFERENCES.PREFERENCES-SAVED');
            this.notify.success(message);

            return notificationPrefsActions.processAuthCodeForPhoneComplete({ preferences });
          }),
          catchError(err => of(notificationPrefsActions.setError({ err })))
        );
      })
    )
  );

  processAuthCodeForWhatsApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationPrefsActions.processAuthCodeForWhatsApp),
      switchMap(action => {
        return this.notificationPrefsApiService.processAuthCodeForField(action.orgCode, action.code, action.userKey, action.fieldName).pipe(
          map(preferences => {
            const message = this.translateService.instant('NOTIFICATION-PREFERENCES.PREFERENCES-SAVED');
            this.notify.success(message);

            return notificationPrefsActions.processAuthCodeForWhatsAppComplete({ preferences });
          }),
          catchError(err => of(notificationPrefsActions.setError({ err })))
        );
      })
    )
  );

  logoutPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationPrefsActions.logoutPreferences),
      withLatestFrom(this.store$.select(fromRouting.selectCurrentRoute)),
      switchMap(([action, route]) => {
        const orgCode = route.params.orgCode;

        return [notificationPrefsActions.logoutPreferencesComplete(), fromRouting.go({ routeData: { path: ['preferences', orgCode] } })];
      })
    )
  );
}
